import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { insightsStrings } from '../../content/insights';
import { externalSharePreferenceOptions, resolveExternalSharingPreferences } from '../../content/shareHighlights';
import { getHobbyMediaStyle, HobbyMediaIcon } from '../../content/hobbyMedia';
import { GET_INSIGHTS_SUMMARY } from '../../views/insights/operations';
import { GET_MY_PROFILE, UPDATE_MY_PROFILE } from '../../views/account/operations';
import { trackTelemetry } from '../../services/telemetry';
import AccessibleModal from '../common/AccessibleModal';
import { CameraIcon, PencilIcon } from '../common/ActionIcons';

const strings = insightsStrings.shareRecap;
const PERIODS = [
  { value: '7d', label: strings.weekly },
  { value: '30d', label: strings.monthly },
  { value: 'year', label: strings.yearly },
];
const FORMATS = {
  post: { label: strings.post, ratio: '4:5', width: 1080, height: 1350 },
  story: { label: strings.story, ratio: '9:16', width: 1080, height: 1920 },
};
const RECAP_OPTIONS = externalSharePreferenceOptions.filter(({ key }) => !['useEntryPhotos', 'includeReviews', 'showPlaceNames'].includes(key));

/**
 * Performs the supports native file sharing operation.
 *
 * @returns {*} The derived operation result.
 */
function supportsNativeFileSharing() {
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function' || typeof navigator.canShare !== 'function') return false;
  try {
    return navigator.canShare({ files: [new File([''], 'hobtalk-share.png', { type: 'image/png' })] });
  } catch {
    return false;
  }
}

/**
 * Performs the story context label operation.
 *
 * @param {string} storyType - story type value.
 * @returns {*} The derived operation result.
 */
function storyContextLabel(storyType) {
  if (storyType === 'milestone') return strings.milestone;
  if (storyType === 'focus') return strings.hobbyFocus;
  return strings.hobbyMoment;
}

/**
 * Performs the exact period label operation.
 *
 * @param {*} windowValue - window value value.
 * @returns {*} The derived operation result.
 */
function exactPeriodLabel(windowValue) {
  const end = new Date();
  let start;
  if (windowValue === 'year') start = new Date(end.getFullYear(), 0, 1);
  else {
    start = new Date(end);
    start.setDate(end.getDate() - (windowValue === '7d' ? 6 : 29));
  }
  const formatter = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });
  return `${formatter.format(start)} - ${formatter.format(end)}`;
}

/**
 * Creates insights recap share model.
 *
 * @param {*} summary - summary value.
 * @param {*} windowValue - window value value.
 * @param {*} preferences - preferences value.
 * @param {string} displayName - display name value.
 * @returns {*} The derived operation result.
 */
export function buildInsightsRecapShareModel(summary, windowValue, preferences, displayName = '') {
  const recap = summary?.shareRecap || {};
  const availableHobbies = (recap.hobbies || []).filter((item) => Number(item.activityCount || 0) > 0);
  let metrics = [...(recap.metrics || [])]
    .filter((metric) => preferences.includeRatings || !String(metric.label).toLowerCase().includes('rating'));
  if (!preferences.includeDetailedMetrics) metrics = metrics.slice(0, 2);
  const hobbies = [...availableHobbies]
    .slice(0, preferences.includeDetailedMetrics ? 5 : 3)
    .map((item) => ({ id: item.hobbyId, name: item.hobby, value: Number(item.activityCount || 0) }));
  const periodNames = { '7d': 'Weekly recap', '30d': 'Monthly recap', year: 'Yearly recap' };
  return {
    eyebrow: recap.eyebrow || strings.storyEyebrow,
    storyType: recap.storyType || (hobbies.length > 1 ? 'mix' : hobbies[0]?.value > 1 ? 'focus' : 'moment'),
    primaryHobbyId: recap.primaryHobbyId || hobbies[0]?.id || 'hobby',
    periodLabel: preferences.showExactDates ? exactPeriodLabel(windowValue) : periodNames[windowValue] || periodNames['30d'],
    title: recap.title || insightsStrings.recap.defaultTitle,
    summary: recap.summary || insightsStrings.empty.detail,
    achievement: recap.achievement || { kind: 'moments', label: 'Hobby moments', value: '0', detail: 'Meaningful activity worth remembering' },
    metrics,
    hobbies,
    displayName: preferences.showDisplayName ? displayName : '',
  };
}

/**
 * Performs the rounded rect operation.
 *
 * @param {*} context - context value.
 * @param {*} x - x value.
 * @param {*} y - y value.
 * @param {*} width - width value.
 * @param {*} height - height value.
 * @param {*} radius - radius value.
 * @param {*} color - color value.
 * @returns {void} No value; the operation applies its documented side effects.
 */
function roundedRect(context, x, y, width, height, radius, color) {
  context.fillStyle = color;
  context.beginPath();
  context.roundRect(x, y, width, height, radius);
  context.fill();
}

/**
 * Performs the wrap text operation.
 *
 * @param {*} context - context value.
 * @param {*} text - text value.
 * @param {*} x - x value.
 * @param {*} y - y value.
 * @param {*} maxWidth - max width value.
 * @param {*} lineHeight - line height value.
 * @param {*} maxLines - max lines value.
 * @returns {*} The derived operation result.
 */
function wrapText(context, text, x, y, maxWidth, lineHeight, maxLines) {
  const words = String(text || '').split(/\s+/);
  const lines = [];
  let current = '';
  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (context.measureText(next).width > maxWidth && current) {
      lines.push(current);
      current = word;
    } else current = next;
  });
  if (current) lines.push(current);
  lines.slice(0, maxLines).forEach((line, index) => context.fillText(line, x, y + index * lineHeight));
  return Math.min(lines.length, maxLines) * lineHeight;
}

/**
 * Retrieves image.
 *
 * @param {string} url - URL used by the operation.
 * @returns {Promise<*>} A promise resolving to the completed operation result.
 */
async function loadImage(url) {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = url;
  });
}

/**
 * Performs the draw cover image operation.
 *
 * @param {*} context - context value.
 * @param {*} image - image value.
 * @param {*} x - x value.
 * @param {*} y - y value.
 * @param {*} width - width value.
 * @param {*} height - height value.
 * @param {*} adjustment - adjustment value.
 * @returns {void} No value; the operation applies its documented side effects.
 */
function drawCoverImage(context, image, x, y, width, height, adjustment) {
  const baseScale = Math.max(width / image.width, height / image.height);
  const zoom = Math.max(Number(adjustment?.zoom || 1), 1);
  const drawnWidth = image.width * baseScale * zoom;
  const drawnHeight = image.height * baseScale * zoom;
  const overflowX = Math.max(drawnWidth - width, 0);
  const overflowY = Math.max(drawnHeight - height, 0);
  const offsetX = Number(adjustment?.horizontal || 0) * overflowX / 2;
  const offsetY = Number(adjustment?.vertical || 0) * overflowY / 2;
  context.save();
  context.beginPath();
  context.roundRect(x, y, width, height, 24);
  context.clip();
  context.drawImage(image, x - overflowX / 2 - offsetX, y - overflowY / 2 - offsetY, drawnWidth, drawnHeight);
  context.restore();
}

/**
 * Performs the draw fallback visual operation.
 *
 * @param {*} context - context value.
 * @param {*} model - model value.
 * @param {*} x - x value.
 * @param {*} y - y value.
 * @param {*} width - width value.
 * @param {*} height - height value.
 * @returns {void} No value; the operation applies its documented side effects.
 */
function drawFallbackVisual(context, model, x, y, width, height) {
  const hobbies = model.hobbies.length ? model.hobbies.slice(0, 3) : [{ id: model.primaryHobbyId, name: 'Hobby' }];
  const gradient = context.createLinearGradient(x, y, x + width, y + height);
  hobbies.forEach((hobby, index) => gradient.addColorStop(index / Math.max(hobbies.length, 1), getHobbyMediaStyle(hobby.id).accent + '55'));
  gradient.addColorStop(1, '#0b2437');
  roundedRect(context, x, y, width, height, 24, gradient);
  const center = x + width / 2;
  const spacing = Math.min(width / (hobbies.length + 1), 150);
  hobbies.forEach((hobby, index) => {
    const itemX = center + (index - (hobbies.length - 1) / 2) * spacing;
    context.fillStyle = 'rgba(7,11,20,.58)';
    context.beginPath();
    context.arc(itemX, y + height / 2, 48, 0, Math.PI * 2);
    context.fill();
    context.fillStyle = getHobbyMediaStyle(hobby.id).accent;
    context.font = '800 34px system-ui, sans-serif';
    context.textAlign = 'center';
    context.fillText(String(hobby.name || 'H').slice(0, 1).toUpperCase(), itemX, y + height / 2 + 12);
  });
  context.textAlign = 'left';
}

/**
 * Renders recap.
 *
 * @param {*} model - model value.
 * @param {*} format - format value.
 * @param {string} photoUrl - photo url value.
 * @param {*} adjustment - adjustment value.
 * @param {*} personalNote - personal note value.
 * @returns {Promise<*>} A promise resolving to the completed operation result.
 */
async function renderRecap(model, format, photoUrl, adjustment, personalNote) {
  const config = FORMATS[format];
  const canvas = document.createElement('canvas');
  canvas.width = config.width;
  canvas.height = config.height;
  const context = canvas.getContext('2d');
  const gradient = context.createLinearGradient(0, 0, config.width, config.height);
  gradient.addColorStop(0, '#07101f');
  gradient.addColorStop(1, '#0b2437');
  context.fillStyle = gradient;
  context.fillRect(0, 0, config.width, config.height);
  const padding = 70;
  let y = 72;
  const logo = await loadImage('/assets/brand/hobtalk-mark-transparent-192.png');
  if (logo) context.drawImage(logo, padding, y - 10, 56, 56);
  context.fillStyle = '#ffffff';
  context.font = '700 38px system-ui, sans-serif';
  context.fillText('Hobtalk', padding + 72, y + 30);
  context.textAlign = 'right';
  context.fillStyle = '#22d3ee';
  context.font = '800 22px system-ui, sans-serif';
  context.fillText(model.eyebrow, config.width - padding, y + 26);
  context.textAlign = 'left';
  y += format === 'story' ? 105 : 82;
  const visualHeight = format === 'story'
    ? (model.hobbies.length > 1 ? 820 : 870)
    : (model.hobbies.length > 1 ? 375 : 435);
  const photo = photoUrl ? await loadImage(photoUrl) : null;
  if (photo) drawCoverImage(context, photo, padding, y, config.width - padding * 2, visualHeight, adjustment);
  else drawFallbackVisual(context, model, padding, y, config.width - padding * 2, visualHeight);
  y += visualHeight + 34;
  context.fillStyle = '#22d3ee';
  context.font = '800 22px system-ui, sans-serif';
  context.fillText(model.periodLabel.toUpperCase(), padding, y);
  y += 55;
  context.fillStyle = '#ffffff';
  context.font = `700 ${format === 'story' ? 64 : 54}px system-ui, sans-serif`;
  y += wrapText(context, model.title, padding, y, config.width - padding * 2, format === 'story' ? 72 : 62, 2);
  context.fillStyle = 'rgba(255,255,255,.72)';
  context.font = '500 28px system-ui, sans-serif';
  const storyCopy = String(personalNote || '').trim() || model.summary;
  y += 18 + wrapText(context, storyCopy, padding, y + 18, config.width - padding * 2, 38, format === 'story' ? 3 : 2);
  y += 24;
  roundedRect(context, padding, y, config.width - padding * 2, 142, 20, 'rgba(255,255,255,.1)');
  context.fillStyle = '#22d3ee';
  context.font = '800 52px system-ui, sans-serif';
  context.fillText(model.achievement.value, padding + 28, y + 64);
  context.fillStyle = '#ffffff';
  context.font = '750 24px system-ui, sans-serif';
  context.fillText(model.achievement.label, padding + 28, y + 98);
  context.fillStyle = 'rgba(255,255,255,.58)';
  context.font = '500 18px system-ui, sans-serif';
  context.fillText(model.achievement.detail, padding + 28, y + 124);
  y += 162;
  const gap = 14;
  const supportingMetrics = model.metrics.slice(0, 3);
  const metricWidth = (config.width - padding * 2 - gap * Math.max(supportingMetrics.length - 1, 0)) / Math.max(supportingMetrics.length, 1);
  supportingMetrics.forEach((metric, index) => {
    const x = padding + index * (metricWidth + gap);
    roundedRect(context, x, y, metricWidth, 94, 18, 'rgba(255,255,255,.075)');
    context.fillStyle = 'rgba(255,255,255,.56)';
    context.font = '800 17px system-ui, sans-serif';
    context.fillText(metric.label.toUpperCase(), x + 16, y + 30);
    context.fillStyle = '#ffffff';
    context.font = '700 30px system-ui, sans-serif';
    context.fillText(metric.value, x + 16, y + 70);
  });
  y += supportingMetrics.length ? 116 : 10;
  if (model.hobbies.length > 1) {
    context.fillStyle = 'rgba(255,255,255,.56)';
    context.font = '800 17px system-ui, sans-serif';
    context.fillText(strings.hobbyMix.toUpperCase(), padding, y);
    y += 22;
    const total = Math.max(model.hobbies.reduce((sum, item) => sum + item.value, 0), 1);
    let x = padding;
    model.hobbies.forEach((hobby) => {
      const width = (config.width - padding * 2) * hobby.value / total;
      roundedRect(context, x, y, Math.max(width - 4, 6), 20, 8, getHobbyMediaStyle(hobby.id).accent);
      x += width;
    });
    y += 46;
    const legendWidth = (config.width - padding * 2) / model.hobbies.length;
    model.hobbies.forEach((hobby, index) => {
      const legendX = padding + index * legendWidth;
      context.fillStyle = getHobbyMediaStyle(hobby.id).accent;
      context.beginPath();
      context.arc(legendX + 7, y, 7, 0, Math.PI * 2);
      context.fill();
      context.fillStyle = 'rgba(255,255,255,.74)';
      context.font = '650 18px system-ui, sans-serif';
      context.fillText(hobby.name, legendX + 22, y + 6, legendWidth - 26);
    });
    y += 32;
  } else {
    const hobby = model.hobbies[0] || { id: model.primaryHobbyId, name: getHobbyMediaStyle(model.primaryHobbyId).label };
    const style = getHobbyMediaStyle(hobby.id);
    context.fillStyle = style.accent;
    context.beginPath();
    context.arc(padding + 14, y + 6, 10, 0, Math.PI * 2);
    context.fill();
    context.fillStyle = 'rgba(255,255,255,.58)';
    context.font = '800 18px system-ui, sans-serif';
    context.fillText(storyContextLabel(model.storyType).toUpperCase(), padding + 34, y + 12);
    context.fillStyle = 'rgba(255,255,255,.84)';
    context.font = '650 22px system-ui, sans-serif';
    context.fillText(hobby.name || style.label, padding + 34, y + 40);
  }
  const footerY = config.height - 72;
  context.fillStyle = 'rgba(255,255,255,.72)';
  context.font = '600 22px system-ui, sans-serif';
  const footer = model.displayName ? `${model.displayName} · ${strings.footer}` : strings.footer;
  context.fillText(footer, padding, footerY);
  context.textAlign = 'right';
  context.fillText(strings.website, config.width - padding, footerY);
  context.textAlign = 'left';
  return new Promise((resolve, reject) => canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Unable to render recap'))), 'image/png'));
}

/**
 * Renders the recap preview experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.model - model value.
 * @param {*} props.format - format value.
 * @param {*} props.photoUrl - photo url value.
 * @param {*} props.adjustment - adjustment value.
 * @param {*} props.personalNote - personal note value.
 * @param {*} props.onPhotoClick - Callback invoked when photo click occurs.
 * @param {*} props.onMessageClick - Callback invoked when message click occurs.
 * @param {*} props.isMessageEditing - Whether is message editing is enabled.
 * @returns {JSX.Element} The rendered interface.
 */
function RecapPreview({ model, format, photoUrl, adjustment, personalNote, onPhotoClick, onMessageClick, isMessageEditing }) {
  const total = Math.max(model.hobbies.reduce((sum, item) => sum + item.value, 0), 1);
  const storyCopy = personalNote.trim() || model.summary;
  const primaryHobby = model.hobbies[0] || { id: model.primaryHobbyId, name: getHobbyMediaStyle(model.primaryHobbyId).label };
  return (
    <div className={`insights-share-preview ${format}`}>
      <header><span><img src="/assets/brand/hobtalk-mark-transparent-192.png" alt="" />Hobtalk</span><b>{model.eyebrow}</b></header>
      <div className="insights-share-visual">
        {photoUrl
          ? <img src={photoUrl} alt="" style={{ transform: `scale(${adjustment.zoom}) translate(${-adjustment.horizontal * 12}%, ${-adjustment.vertical * 12}%)` }} />
          : <div className="insights-share-art">{(model.hobbies.length ? model.hobbies : [primaryHobby]).slice(0, 3).map((hobby) => <span key={hobby.id} style={{ color: getHobbyMediaStyle(hobby.id).accent }}><HobbyMediaIcon type={hobby.id} /></span>)}</div>}
        <button type="button" className="insights-share-photo-button" onClick={onPhotoClick} aria-label={photoUrl ? strings.changePhoto : strings.addPhoto}><CameraIcon /></button>
      </div>
      <div className="insights-share-copy"><span>{model.periodLabel}</span><h3>{model.title}</h3><button type="button" className={`insights-share-message-preview ${isMessageEditing ? 'editing' : ''}`} onClick={onMessageClick} aria-label={strings.editMessage}><span>{storyCopy}</span><PencilIcon /></button></div>
      <div className="insights-share-achievement"><strong>{model.achievement.value}</strong><span><b>{model.achievement.label}</b><small>{model.achievement.detail}</small></span></div>
      {model.metrics.length > 0 && <div className={`insights-share-metrics metric-count-${Math.min(model.metrics.length, 3)}`}>{model.metrics.map((metric) => <div key={metric.label}><small>{metric.label}</small><strong>{metric.value}</strong></div>)}</div>}
      {model.hobbies.length > 1 && <div className="insights-share-hobbies">
        <small>{strings.hobbyMix}</small>
        <div className="insights-share-mix">{model.hobbies.map((hobby) => <i key={hobby.id} style={{ flex: hobby.value / total, background: getHobbyMediaStyle(hobby.id).accent }} />)}</div>
        <div className="insights-share-legend">{model.hobbies.map((hobby) => <span key={hobby.id}><i style={{ background: getHobbyMediaStyle(hobby.id).accent }} />{hobby.name}</span>)}</div>
      </div>}
      {model.hobbies.length <= 1 && <div className="insights-share-single-hobby"><span style={{ color: getHobbyMediaStyle(primaryHobby.id).accent }}><HobbyMediaIcon type={primaryHobby.id} /></span><small><b>{storyContextLabel(model.storyType)}</b>{primaryHobby.name}</small></div>}
      <footer><span>{model.displayName ? `${model.displayName} · ` : ''}{strings.footer}</span><small>{strings.website}</small></footer>
    </div>
  );
}

/**
 * Renders the insights recap share modal experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.initialSummary - initial summary value.
 * @param {*} props.initialWindow - initial window value.
 * @param {*} props.onClose - Callback invoked when close occurs.
 * @param {*} props.onShared - Callback invoked when shared occurs.
 * @returns {JSX.Element} The rendered interface.
 */
export default function InsightsRecapShareModal({ initialSummary, initialWindow, onClose, onShared }) {
  const supportedWindow = ['7d', '30d', 'year'].includes(initialWindow) ? initialWindow : '30d';
  const [windowValue, setWindowValue] = useState(supportedWindow);
  const [format, setFormat] = useState('post');
  const [showDetails, setShowDetails] = useState(false);
  const [message, setMessage] = useState('');
  const [isPreparing, setIsPreparing] = useState(false);
  const [photoUrl, setPhotoUrl] = useState('');
  const [showPhotoSources, setShowPhotoSources] = useState(false);
  const [showPhotoEditor, setShowPhotoEditor] = useState(false);
  const [personalNote, setPersonalNote] = useState('');
  const [isMessageEditing, setIsMessageEditing] = useState(false);
  const [adjustments, setAdjustments] = useState({
    post: { zoom: 1, horizontal: 0, vertical: 0 },
    story: { zoom: 1, horizontal: 0, vertical: 0 },
  });
  const photoInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const messageEditorRef = useRef(null);
  const { data, previousData, loading } = useQuery(GET_INSIGHTS_SUMMARY, { variables: { window: windowValue }, fetchPolicy: 'cache-and-network', nextFetchPolicy: 'cache-first' });
  const { data: profileData } = useQuery(GET_MY_PROFILE);
  const [updateProfile, { loading: savingDefaults }] = useMutation(UPDATE_MY_PROFILE, { refetchQueries: [{ query: GET_MY_PROFILE }] });
  const savedPreferences = resolveExternalSharingPreferences(profileData?.getMyProfile?.externalSharingPreferences).insightRecaps;
  const [preferences, setPreferences] = useState(savedPreferences);
  const summary = data?.getInsightsSummary || previousData?.getInsightsSummary || (windowValue === initialWindow ? initialSummary : null);
  const model = useMemo(() => buildInsightsRecapShareModel(summary, windowValue, preferences, profileData?.getMyProfile?.displayName || ''), [summary, windowValue, preferences, profileData]);
  const canShareFiles = useMemo(supportsNativeFileSharing, []);

  useEffect(() => {
    setPreferences(savedPreferences);
  }, [profileData?.getMyProfile?.externalSharingPreferences]);

  useEffect(() => () => {
    if (photoUrl) URL.revokeObjectURL(photoUrl);
  }, [photoUrl]);

  useEffect(() => {
    if (isMessageEditing) messageEditorRef.current?.focus({ preventScroll: true });
  }, [isMessageEditing]);

  /**
   * Performs photo.
   *
   * @param {*} event - Browser or React event that triggered the action.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const selectPhoto = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setPhotoUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return URL.createObjectURL(file);
    });
    setShowPhotoSources(false);
    setShowPhotoEditor(true);
  };

  /**
   * Updates adjustment.
   *
   * @param {string} key - key value.
   * @param {*} value - Value to inspect or transform.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const updateAdjustment = (key, value) => {
    setAdjustments((current) => ({
      ...current,
      [format]: { ...current[format], [key]: Number(value) },
    }));
  };

  /**
   * Performs defaults.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const saveDefaults = async () => {
    setMessage('');
    try {
      await updateProfile({ variables: { input: { externalSharingPreferences: { insightRecaps: preferences } } } });
      setMessage(strings.defaultsSaved);
    } catch {
      setMessage('We could not save those defaults. Please try again.');
    }
  };

  /**
   * Performs the export recap operation.
   *
   * @param {*} action - action value.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const exportRecap = async (action) => {
    setIsPreparing(true);
    setMessage('');
    try {
      const blob = await renderRecap(model, format, photoUrl, adjustments[format], personalNote);
      const file = new File([blob], `hobtalk-${windowValue}-recap.png`, { type: 'image/png' });
      trackTelemetry('insights.recap.generated', { window: windowValue, layout: format });
      if (action === 'share' && canShareFiles) {
        await navigator.share({ files: [file], title: model.title, text: `${strings.footer} · https://${strings.website}` });
        trackTelemetry('insights.recap.shared', { window: windowValue, layout: format });
        onShared?.();
      } else {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = file.name;
        link.click();
        URL.revokeObjectURL(url);
        onShared?.();
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;
      setMessage('We could not prepare this recap. Please try again.');
    } finally {
      setIsPreparing(false);
    }
  };

  return (
    <AccessibleModal
      className="insights-share-modal"
      overlayClassName="insights-share-overlay"
      labelledBy="insights-share-title"
      onClose={onClose}
      closeOnEscape={!showPhotoSources}
    >
        <header className="insights-share-modal-header"><div><p className="eyebrow">Share insights</p><h2 id="insights-share-title" tabIndex="-1" data-modal-initial-focus>{strings.title}</h2></div><button type="button" className="icon-action" onClick={onClose} aria-label="Close">×</button></header>
        <div className="insights-share-controls">
          <label><span>{strings.period}</span><select value={windowValue} onChange={(event) => setWindowValue(event.target.value)}>{PERIODS.map((period) => <option key={period.value} value={period.value}>{period.label}</option>)}</select></label>
          <div><span>{strings.layout}</span><div className="segmented-control insights-layout-selector" role="radiogroup" aria-label={strings.layout}>{Object.entries(FORMATS).map(([key, value]) => <button type="button" role="radio" aria-checked={format === key} className={format === key ? 'active' : ''} key={key} onClick={() => setFormat(key)}><span><strong>{value.label}</strong><small>{value.ratio}</small></span></button>)}</div></div>
        </div>
        <div className="insights-share-preview-wrap">
          <div className={`insights-share-preview-stage ${format}`}>
            {summary ? <RecapPreview model={model} format={format} photoUrl={photoUrl} adjustment={adjustments[format]} personalNote={personalNote} onPhotoClick={() => setShowPhotoSources(true)} onMessageClick={() => setIsMessageEditing(true)} isMessageEditing={isMessageEditing} /> : <div className="insights-share-loading">Loading recap…</div>}
            {summary && isMessageEditing && <section className="insights-share-message-editor" aria-label={strings.messageLabel}>
              <header><span><strong>{strings.messageLabel}</strong><small>{strings.messageHelper}</small></span><button type="button" onClick={() => setIsMessageEditing(false)}>{strings.doneEditing}</button></header>
              <textarea ref={messageEditorRef} maxLength="80" rows="2" value={personalNote || model.summary} onChange={(event) => setPersonalNote(event.target.value)} />
              <footer><button type="button" onClick={() => setPersonalNote('')}>{strings.useSuggestedMessage}</button><span>{(personalNote || model.summary).length}/80</span></footer>
            </section>}
          </div>
          {loading && summary && <div className="insights-share-refreshing" aria-label="Refreshing recap" />}
        </div>
        {photoUrl && <section className="share-highlight-inline-editor" aria-label="Adjust photo">
          <header><strong>Adjust photo</strong><button type="button" className="icon-action" aria-label="Close" onClick={() => setShowPhotoEditor((value) => !value)}>{showPhotoEditor ? '−' : '+'}</button></header>
          {showPhotoEditor && <>
            <label>Zoom<input type="range" min="1" max="2.5" step="0.01" value={adjustments[format].zoom} onChange={(event) => updateAdjustment('zoom', event.target.value)} /></label>
            <label>Horizontal position<input type="range" min="-1" max="1" step="0.01" value={adjustments[format].horizontal} onChange={(event) => updateAdjustment('horizontal', event.target.value)} /></label>
            <label>Vertical position<input type="range" min="-1" max="1" step="0.01" value={adjustments[format].vertical} onChange={(event) => updateAdjustment('vertical', event.target.value)} /></label>
          </>}
        </section>}
        <button type="button" className="share-highlight-details-toggle" onClick={() => setShowDetails((value) => !value)} aria-expanded={showDetails}><span><strong>{strings.sharedDetails}</strong><small>{strings.sharedDetailsSummary}</small></span><b aria-hidden="true">{showDetails ? '−' : '+'}</b></button>
        {showDetails && <div className="share-highlight-shared-details">{RECAP_OPTIONS.map((option) => <label key={option.key}><span>{option.label}</span><input type="checkbox" checked={Boolean(preferences[option.key])} onChange={(event) => setPreferences((current) => ({ ...current, [option.key]: event.target.checked }))} /></label>)}<button type="button" className="btn-secondary" disabled={savingDefaults} onClick={saveDefaults}>{savingDefaults ? 'Saving…' : strings.saveDefaults}</button></div>}
        {message && <p className="muted-copy">{message}</p>}
        <div className={`insights-share-export-actions ${canShareFiles ? '' : 'single'}`.trim()}>
          {canShareFiles && <button type="button" className="btn-primary" disabled={!summary || loading || isPreparing} onClick={() => exportRecap('share')}>{isPreparing ? strings.preparing : strings.share}</button>}
          <button type="button" className={canShareFiles ? 'btn-secondary' : 'btn-primary'} disabled={!summary || loading || isPreparing} onClick={() => exportRecap('download')}>{isPreparing ? strings.preparing : strings.downloadImage}</button>
        </div>
        <input ref={photoInputRef} hidden type="file" accept="image/*" onChange={selectPhoto} />
        <input ref={cameraInputRef} hidden type="file" accept="image/*" capture="environment" onChange={selectPhoto} />
        {showPhotoSources && <AccessibleModal className="share-photo-dialog" overlayClassName="nested-accessible-modal-overlay" labelledBy="recap-photo-source-title" onClose={() => setShowPhotoSources(false)}>
            <header><h3 id="recap-photo-source-title">Photo</h3><button type="button" className="icon-action" aria-label="Close" onClick={() => setShowPhotoSources(false)}>×</button></header>
            <button type="button" onClick={() => cameraInputRef.current?.click()}>Take photo</button>
            <button type="button" onClick={() => photoInputRef.current?.click()}>Choose photo</button>
            {photoUrl && <button type="button" className="destructive" onClick={() => { URL.revokeObjectURL(photoUrl); setPhotoUrl(''); setShowPhotoSources(false); }}>Use Hobtalk artwork</button>}
            <p className="share-photo-note">This photo is used only for this share and is not uploaded to Hobtalk.</p>
        </AccessibleModal>}
    </AccessibleModal>
  );
}
